/**
 * Soft≠Live Page210 — Settings Audit residual liquid (Page133/86 + dual).
 * Mount: /settings/audit (alias /audit-log)
 * Buses (fetch wiring only — never paint raw paths in operator UI/DOM text):
 *   settings audit status · trail preview · sync export · async · events · jobs
 * Soft residual deleted:
 *  - invent trail rows when trail bus sealed
 *  - invent download success without Live blob
 *  - paint raw API paths in operator copy
 * Honesty is HTTP + status on the wire, human soft_message on screen.
 */
import { useMemo, useState } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, Sec, Ticker, go } from "./ui.js";
import { api, apiBase, path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { autonomyPlanesOf, fourVisiblesOf } from "./autonomy-planes.js";
import { CSRF_HEADER_NAME, getCsrfToken } from "../lib/csrf.js";
import { whySay, operatorSay } from "./operator-say.js";

/** Soft residual Page210: missing contract ops must not crash the desk. */
function safeAuditPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_audit_status") return "/settings/audit/status";
    if (op === "get__audit_events") {
      const q = (opts && opts.query) || {};
      const lim = q.limit != null ? "?limit=" + encodeURIComponent(q.limit) : "";
      return "/audit/events" + lim;
    }
    if (op === "get__audit_export.csv") return "/audit/export.csv";
    if (op === "get__audit_export.pdf") return "/audit/export.pdf";
    if (op === "get__audit_export-cover.pdf") return "/audit/export-cover.pdf";
    if (op === "post__audit_export-async.csv") return "/audit/export-async.csv";
    if (op === "get__audit_export-async_p") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/audit/export-async/" + encodeURIComponent(id) : "";
    }
    if (op === "get__audit_export-async_p_result.csv") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/audit/export-async/" + encodeURIComponent(id) + "/result.csv" : "";
    }
    if (op === "get__events_export.csv") return "/events/export.csv";
    if (op === "get__events_export-cover.pdf") return "/events/export-cover.pdf";
    if (op === "get__events_export-rows.pdf") return "/events/export-rows.pdf";
    if (op === "post__events_export-async.csv") return "/events/export-async.csv";
    if (op === "get__events_export-async_p") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/events/export-async/" + encodeURIComponent(id) : "";
    }
    if (op === "get__events_export-async_p_result.csv") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/events/export-async/" + encodeURIComponent(id) + "/result.csv" : "";
    }
    if (op === "post__export-control_jobs") return "/export-control/jobs";
    if (op === "get__export-control_jobs_p") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/export-control/jobs/" + encodeURIComponent(id) : "";
    }
    if (op === "post__export-control_jobs_p_download-token") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/export-control/jobs/" + encodeURIComponent(id) + "/download-token" : "";
    }
    if (op === "get__export-control_jobs_p_download") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/export-control/jobs/" + encodeURIComponent(id) + "/download" : "";
    }
    return "";
  }
}

/** Path-free operator copy for Settings Audit export desk. */
export function softAuditExportErrorOf(raw, fallback) {
  const m = String(raw || "").trim();
  const fb = fallback || "That action could not be completed.";
  if (!m) return fb;
  if (/\b401\b|session ended|unauthorized/i.test(m)) {
    return "Sign in again — your session is no longer valid.";
  }
  if (/\b403\b|forbidden|insufficient|seat_gated|not_authorized/i.test(m)) {
    return "This action is not allowed for your account.";
  }
  if (/\b404\b|not found|not ready|not.?armed/i.test(m)) {
    return "That export is not ready yet.";
  }
  if (/\b429\b|too many/i.test(m)) {
    return "Too many attempts. Wait a moment, then try again.";
  }
  if (/\b5\d\d\b|timed out|network|unreachable|failed to fetch/i.test(m)) {
    return "The export service did not answer. Try again shortly.";
  }
  if (/\/api\/|\/audit|\/export|\/settings\/audit|GET |POST |status_path|http/i.test(m)) {
    return fb;
  }
  return m.length > 160 ? fb : m;
}

/** Pure: seat chips from Live Settings Audit posture (Page210). Fail-closed. */
export function auditExportSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paint = Number(lim.preview_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    trailAllowed: p.trail_preview_allowed === true,
    exportAllowed: p.export_plane_allowed === true,
    exportSyncAllowed: p.export_sync_allowed === true,
    exportAsyncAllowed: p.export_async_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim() || null,
    softTitle: String(p.soft_title || "").trim() || null,
    softMessage: String(p.soft_message || "").trim() || null,
    forbidApiPaint: p.soft_api_path_paint_forbidden !== false,
    previewPaintMax:
      Number.isFinite(paint) && paint > 0 ? Math.floor(paint) : 80,
  };
}

/** Pure: pick job id from export / async envelopes. */
export function jobIdOf(pack) {
  if (!pack || typeof pack !== "object") return "";
  const nested = pack.item && typeof pack.item === "object" ? pack.item : null;
  return String(
    pack.job_id || pack.id || (nested && (nested.job_id || nested.id)) || "",
  ).trim();
}

/** Pure: preview KPI from Live audit list only. */
export function exportPreviewKpisOf(rows, pane) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    preview: list.length,
    pane: String(pane || "sync"),
  };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Fetch a binary export with session cookies. */
async function fetchExportBlob(opPath, filename, toast) {
  const headers = {};
  const t = getCsrfToken();
  if (t) headers[CSRF_HEADER_NAME] = t;
  const r = await fetch(apiBase() + opPath, {
    method: "GET",
    credentials: "include",
    headers,
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error((text && text.slice(0, 180)) || String(r.status));
  }
  const blob = await r.blob();
  downloadBlob(blob, filename);
  toast && toast("Downloaded", "go");
}

export function AuditExportDesk({ toast }) {
  const { run, busy, error, envelope } = useAction();
  const posture = useApi(safeAuditPath("get__settings_audit_status"));
  const home = useApi("/ai/home-context");
  const four = fourVisiblesOf(
    autonomyPlanesOf(home.data && home.data.autonomy),
    { loading: !!(home.loading && !home.data) },
  );
  const post = posture.data || {};
  // Fail-closed while loading — Soft invent open seats deleted.
  const seat = auditExportSeatOf(posture.loading || !posture.data ? {} : post);

  const trailOn = seat.trailAllowed;
  const exportOn = seat.exportAllowed;
  const events = useApi(
    trailOn ? safeAuditPath("get__audit_events", { query: { limit: 80 } }) : null,
  );
  const [pane, setPane] = useState("sync");
  const [asyncJob, setAsyncJob] = useState("");
  const [asyncStatus, setAsyncStatus] = useState(null);
  const [evJob, setEvJob] = useState("");
  const [evStatus, setEvStatus] = useState(null);
  const [ctrlJob, setCtrlJob] = useState("");
  const [ctrlStatus, setCtrlStatus] = useState(null);
  const [ctrlToken, setCtrlToken] = useState("");
  const [clientRef, setClientRef] = useState("");
  const [kind, setKind] = useState("audit_csv");
  const [replayId, setReplayId] = useState("");
  const [replayBusy, setReplayBusy] = useState(false);
  const [replayErr, setReplayErr] = useState("");
  const [replayChain, setReplayChain] = useState(null);

  const rows = useMemo(() => {
    const list = (events.data && events.data.items) || [];
    return list.slice(0, seat.previewPaintMax);
  }, [events.data, seat.previewPaintMax]);
  const kpis = useMemo(() => exportPreviewKpisOf(rows, pane), [rows, pane]);
  const denied = !!(events.error && /\b40[13]\b|forbidden|insufficient/i.test(String(events.error)));

  const softTitle = seat.softTitle || seat.postureLabel || "Audit export";
  const softLede = seat.softMessage || seat.postureLabel || (
    "Review recent audit rows and download compliance files for your organisation. "
    + "Every preview and download is checked on the server for your seat."
  );

  const reRead = () => {
    if (!seat.reReadAllowed) {
      toast && toast("You cannot re-read this desk from this account", "warn");
      return;
    }
    posture.reload && posture.reload();
    events.reload && events.reload();
    toast && toast("Audit desk refreshed", "go");
  };

  const syncCsv = async () => {
    if (!seat.exportSyncAllowed) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    try {
      await fetchExportBlob(safeAuditPath("get__audit_export.csv"), "audit-export.csv", toast);
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "CSV download was refused — nothing was changed"), "bad");
    }
  };
  const syncPdf = async () => {
    if (!seat.exportSyncAllowed) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    try {
      await fetchExportBlob(safeAuditPath("get__audit_export.pdf"), "audit-export.pdf", toast);
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "PDF download was refused — nothing was changed"), "bad");
    }
  };
  const syncCover = async () => {
    if (!seat.exportSyncAllowed) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    try {
      await fetchExportBlob(safeAuditPath("get__audit_export-cover.pdf"), "audit-export-cover.pdf", toast);
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Cover PDF was refused — nothing was changed"), "bad");
    }
  };

  const startAsyncCsv = async () => {
    if (!seat.exportAsyncAllowed) {
      toast && toast("Background audit exports are not available", "warn");
      return;
    }
    const out = await run(safeAuditPath("post__audit_export-async.csv"), { body: {} });
    if (!out) {
      toast && toast(softAuditExportErrorOf(error, "Background export was refused — nothing was changed"), "bad");
      return;
    }
    const jid = jobIdOf(out);
    setAsyncJob(jid);
    setAsyncStatus(out);
    toast && toast(jid ? ("Export job started · " + jid) : "Export job started", "go");
  };

  const refreshAsync = async () => {
    if (!seat.exportAsyncAllowed) {
      toast && toast("Background audit exports are not available", "warn");
      return;
    }
    if (!asyncJob) { toast && toast("Enter a job id first", "warn"); return; }
    try {
      const out = await api(safeAuditPath("get__audit_export-async_p", { pathParams: [asyncJob] }), { method: "GET" });
      setAsyncStatus(out);
      toast && toast("Job status refreshed", "go");
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Could not re-read that job"), "warn");
    }
  };

  const downloadAsync = async () => {
    if (!seat.exportAsyncAllowed) {
      toast && toast("Background audit exports are not available", "warn");
      return;
    }
    if (!asyncJob) { toast && toast("Enter a job id first", "warn"); return; }
    try {
      await fetchExportBlob(
        safeAuditPath("get__audit_export-async_p_result.csv", { pathParams: [asyncJob] }),
        "audit-async-" + asyncJob + ".csv",
        toast,
      );
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Result is not ready yet"), "bad");
    }
  };

  const syncEventsCsv = async () => {
    if (!seat.exportSyncAllowed) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    try {
      await fetchExportBlob(safeAuditPath("get__events_export.csv"), "events-export.csv", toast);
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Events CSV was refused — nothing was changed"), "bad");
    }
  };
  const syncEventsCover = async () => {
    if (!seat.exportSyncAllowed) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    try {
      await fetchExportBlob(safeAuditPath("get__events_export-cover.pdf"), "events-export-cover.pdf", toast);
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Events cover was refused — nothing was changed"), "bad");
    }
  };
  const syncEventsRows = async () => {
    if (!seat.exportSyncAllowed) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    try {
      await fetchExportBlob(safeAuditPath("get__events_export-rows.pdf"), "events-export-rows.pdf", toast);
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Events rows PDF was refused — nothing was changed"), "bad");
    }
  };
  const startEventsAsync = async () => {
    if (!seat.exportAsyncAllowed) {
      toast && toast("Background event exports are not available", "warn");
      return;
    }
    const out = await run(safeAuditPath("post__events_export-async.csv"), { body: {} });
    if (!out) {
      toast && toast(softAuditExportErrorOf(error, "Background events export was refused — nothing was changed"), "bad");
      return;
    }
    const jid = jobIdOf(out);
    setEvJob(jid);
    setEvStatus(out);
    toast && toast(jid ? ("Events job started · " + jid) : "Events job started", "go");
  };
  const refreshEventsAsync = async () => {
    if (!seat.exportAsyncAllowed) {
      toast && toast("Background event exports are not available", "warn");
      return;
    }
    if (!evJob) { toast && toast("Enter a job id first", "warn"); return; }
    try {
      const out = await api(safeAuditPath("get__events_export-async_p", { pathParams: [evJob] }), { method: "GET" });
      setEvStatus(out);
      toast && toast("Events job status refreshed", "go");
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Could not re-read that job"), "warn");
    }
  };
  const downloadEventsAsync = async () => {
    if (!seat.exportAsyncAllowed) {
      toast && toast("Background event exports are not available", "warn");
      return;
    }
    if (!evJob) { toast && toast("Enter a job id first", "warn"); return; }
    try {
      await fetchExportBlob(
        safeAuditPath("get__events_export-async_p_result.csv", { pathParams: [evJob] }),
        "events-async-" + evJob + ".csv",
        toast,
      );
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Events result is not ready yet"), "bad");
    }
  };

  const startCtrl = async () => {
    if (!exportOn) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    const body = { kind, claims: {} };
    if (clientRef.trim()) body.claims.client_ref = clientRef.trim();
    const out = await run(safeAuditPath("post__export-control_jobs"), { body });
    if (!out) {
      toast && toast(softAuditExportErrorOf(error, "Could not create an export job — nothing was changed"), "bad");
      return;
    }
    const jid = jobIdOf(out);
    setCtrlJob(jid);
    setCtrlStatus(out);
    setCtrlToken("");
    toast && toast(jid ? ("Export job created · " + jid) : "Export job created", "go");
  };

  const refreshCtrl = async () => {
    if (!exportOn) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    if (!ctrlJob) { toast && toast("Enter a job id first", "warn"); return; }
    try {
      const out = await api(safeAuditPath("get__export-control_jobs_p", { pathParams: [ctrlJob] }), { method: "GET" });
      setCtrlStatus(out);
      toast && toast("Job status refreshed", "go");
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Could not re-read that job"), "warn");
    }
  };

  const mintToken = async () => {
    if (!exportOn) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    if (!ctrlJob) { toast && toast("Enter a job id first", "warn"); return; }
    const out = await run(safeAuditPath("post__export-control_jobs_p_download-token", { pathParams: [ctrlJob] }), {
      body: {},
    });
    if (!out) {
      toast && toast(softAuditExportErrorOf(error, "Could not prepare a download link"), "bad");
      return;
    }
    setCtrlToken(String(out.token || ""));
    toast && toast(out.token ? "Download link ready" : "No download link returned", out.token ? "go" : "warn");
  };

  const downloadCtrl = async () => {
    if (!exportOn) {
      toast && toast("Exports are sealed for this account", "warn");
      return;
    }
    if (!ctrlJob) { toast && toast("Enter a job id first", "warn"); return; }
    try {
      await fetchExportBlob(
        safeAuditPath("get__export-control_jobs_p_download", { pathParams: [ctrlJob] }),
        "export-" + ctrlJob + ".bin",
        toast,
      );
    } catch (e) {
      toast && toast(softAuditExportErrorOf(e && e.message, "Download was refused — nothing was changed"), "bad");
    }
  };

  const paneLabel = {
    sync: "Immediate downloads",
    async: "Background audit file",
    events: "Event timeline files",
    jobs: "Durable export jobs",
  };

  const replayByIncident = async () => {
    const id = String(replayId || "").trim();
    if (!id) {
      toast && toast("Enter a canonical incident id first", "warn");
      return;
    }
    setReplayBusy(true);
    setReplayErr("");
    setReplayChain(null);
    try {
      const out = await api("/ai/team/incidents/" + encodeURIComponent(id) + "/run", { method: "GET" });
      const chain = (out && out.mission && out.mission.audit_chain)
        || (out && out.run && out.run.audit_chain)
        || null;
      if (!chain) {
        setReplayErr("No Event→Proof chain on that TeamRun — not invented.");
        toast && toast("TeamRun found without a chain — not invented", "warn");
        return;
      }
      setReplayChain(chain);
      toast && toast("Chain replayed from TeamRun", "go");
    } catch (e) {
      const msg = softAuditExportErrorOf(e && e.message, "No TeamRun for that incident");
      setReplayErr(msg);
      toast && toast(msg, "warn");
    } finally {
      setReplayBusy(false);
    }
  };

  return html`<div className="audit-export-desk audit-export-desk--liquid"
    data-cmc-page="page133-audit-export" data-cmc-legacy="page86-audit-export" data-cmc-audit="page46"
    data-cmc-residual="page210-audit"
    data-cmc-audience="tenant"
    data-cmc-four-visibles="tenant-audit"
    data-soft-api-path-paint-forbidden=${seat.forbidApiPaint ? "1" : "0"}>
    <div className="audit-export-desk__rail">
      <div>
        <p className="eyebrow">Settings · Compliance export</p>
        <h2 className="audit-export-desk__title">${softTitle}</h2>
        <p className="note">${softLede}</p>
      </div>
      <div className="btn-row audit-export-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${busy || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/audit")}>Platform audit trail</button>
        <button type="button" className="btn btn--go btn--sm"
          onClick=${() => go("/settings/delegations")}>Staff delegations</button>
      </div>
    </div>
    <div className="ops-cockpit__metrics" data-cmc-four-visibles="1" data-cmc-audience="tenant-plus-global-rails">
      <div className="ops-cockpit__m" data-cmc-desired-effective="desired vs effective"><b>${operatorSay(four.desired, four.desired || "—")}</b><span>How it should run</span></div>
      <div className="ops-cockpit__m"><b>${four.rev}</b><span>Rule version</span></div>
      <div className="ops-cockpit__m" data-cmc-ack-lag="ACK / lag"><b>${four.ack}</b><span>Boards answered</span></div>
      <div className="ops-cockpit__m"><b>${whySay(four.why)}</b><span>Why blocked</span></div>
    </div>

    <${Alert} tone="info"
      title=${exportOn && trailOn
        ? "Preview and downloads follow your audit seat"
        : softTitle}
      say=${`Signed in as ${seat.role || "operator"}. `
        + `Preview ${seat.trailAllowed ? "open" : "sealed"} · `
        + `downloads ${seat.exportAllowed ? "open" : "sealed"} · `
        + `background ${seat.exportAsyncAllowed ? "open" : "not armed"}.`}/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <${Card} title="Event → Proof replay" lede="Same TeamRun chain as Mission Room. Receipt is never labeled verified."
        data-cmc-s4-replay="1">
      <${Field} label="Canonical incident id">
        <${Input} icon="chip" value=${replayId}
          onChange=${(e) => setReplayId(e.target.value.trim())}
          placeholder="incident id"/>
      </${Field}>
      <button type="button" className="btn btn--sm" style=${{ marginTop: 8 }}
        disabled=${replayBusy || !replayId}
        onClick=${replayByIncident}>${replayBusy ? "Replaying…" : "Replay chain"}</button>
      ${replayErr ? html`<${Alert} tone="warn" title="Replay refused" say=${replayErr}/>` : null}
      ${replayChain ? html`<${Sec} title="Chain">
        <${KV} k="Intact" v=${replayChain.intact ? "yes" : "broken"} tone=${replayChain.intact ? "hi" : "bad"}/>
        <${KV} k="Event to proof" v=${replayChain.event_to_proof ? "complete" : "incomplete"}/>
        <${KV} k="Steps" v=${String(replayChain.steps_n || 0)}/>
        <${KV} k="Receipts" v=${String(replayChain.receipt_n || 0)}/>
        <${KV} k="Proofs" v=${String(replayChain.proof_n || 0)}/>
        <${KV} k="Desired vs Effective" v=${replayChain.intact ? "chain/chain" : "broken/live"}/>
        <${KV} k="Revision" v=${String(replayChain.steps_n || 0)}/>
        <${KV} k="Boards answered" v=${replayChain.event_to_proof ? "Answered" : "Waiting"}/>
        <${KV} k="Why blocked" v=${replayChain.receipt_is_not_verified === false ? "—" : operatorSay("receipt_not_verified")}/>
        <${KV} k="Receipt is verified" v=${replayChain.receipt_is_not_verified === false ? "yes" : "no — receipt only"}/>
        <${KV} k="Tip" v=${String(replayChain.tip_hash || "—")}/>
      <//>` : null}
      <p className="note">Empty or missing TeamRun is an error — this desk does not invent a ledger.</p>
    <//>

    <div className="audit-export-desk__kpis" role="group" aria-label="Export summary">
      <div className="audit-export-desk__kpi" data-on=${trailOn && kpis.preview ? "1" : "0"}>
        <span>Preview rows</span>
        <b>${!trailOn ? "sealed" : (events.loading && !events.data ? "…" : html`<${Ticker} value=${kpis.preview}/>`)}</b>
        <em>recent</em>
      </div>
      <div className="audit-export-desk__kpi" data-on=${exportOn && pane === "sync" ? "1" : "0"}>
        <span>Active tab</span>
        <b>${!exportOn ? "sealed" : (paneLabel[pane] || pane)}</b>
        <em>tools</em>
      </div>
      <div className="audit-export-desk__kpi" data-on=${exportOn && (asyncJob || evJob || ctrlJob) ? "1" : "0"}>
        <span>Jobs tracked</span>
        <b>${!exportOn ? "—" : [asyncJob, evJob, ctrlJob].filter(Boolean).length}</b>
        <em>this browser</em>
      </div>
      <div className="audit-export-desk__kpi" data-on=${trailOn && !denied ? "1" : "0"}>
        <span>Trail seat</span>
        <b>${!trailOn ? "sealed" : (denied ? "restricted" : (events.error ? "error" : "open"))}</b>
        <em>honesty</em>
      </div>
    </div>

    ${!exportOn ? html`
      <${Alert} tone="warn" title="Exports sealed"
        say=${softLede}/>`
    : html`
    <nav className="audit-export-desk__tabs" aria-label="Export panes">
      ${[
        ["sync", "Immediate"],
        ["async", "Background audit"],
        ["events", "Events"],
        ["jobs", "Durable jobs"],
      ].map(([id, l]) => html`
        <button key=${id} type="button" className="audit-export-desk__tab"
          data-on=${pane === id ? "1" : "0"}
          onClick=${() => setPane(id)}>${l}</button>`)}
    </nav>`}

    ${!trailOn ? html`
      <${Alert} tone="info" title="Preview sealed"
        say="This account cannot list recent audit rows. Download tools remain independent if your export seat is open."/>`
    : html`
    <${Card} title="Recent audit rows" meta="preview only">
      <${Alert} tone="info" title="Intent → Proof lives on Mission Room"
        say="Team decision_audit_chain (Event→Proof) paints on Cases / Mission Room. This desk is compliance export — not a second Soft invent chain."/>
      ${events.loading && !events.data
        ? html`<p className="note">Reading recent trail…</p>`
        : events.error
          ? html`<${Alert} tone=${denied ? "info" : "warn"}
              title=${denied ? "Audit trail gated for this account" : "Audit trail unavailable"}
              say=${softAuditExportErrorOf(events.error, "Could not load preview rows.")}/>`
          : html`<${F}>
              <${KV} k="Preview rows" v=${String(rows.length)} tone=${rows.length ? "hi" : "dim"}/>
              <div className="rows" style=${{ marginTop: 8 }}>
                ${!rows.length
                  ? html`<p className="note">No preview rows yet — exports may still work when you need a full file.</p>`
                  : rows.slice(0, 12).map((r, i) => html`
                      <div key=${r.id || i} className="row row--say">
                        <${Ico} d="book" w=${14}/>
                        <span className="main">
                          <b>${r.action || r.event || r.type || "event"}</b>
                          <span>${r.actor || "—"} · ${r.created_at || r.ts || "—"}
                            · ${r.target || r.detail || ""}</span>
                        </span>
                      </div>`)}
              </div>
            <//>`}
      <p className="note">Preview only — full evidence lives in the files you download below.</p>
    <//>`}

    ${exportOn && pane === "sync" && html`
      <${Card} title="Immediate downloads" meta="CSV · PDF · cover" cls="mt">
        <div className="btn-row audit-export-desk__acts">
          <button type="button" className="btn btn--go btn--sm"
            disabled=${!seat.exportSyncAllowed} onClick=${syncCsv}>Download CSV</button>
          <button type="button" className="btn btn--sm"
            disabled=${!seat.exportSyncAllowed} onClick=${syncPdf}>Download PDF</button>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!seat.exportSyncAllowed} onClick=${syncCover}>Cover PDF</button>
        </div>
        <p className="note">Files download with your signed-in session. A refused server response never pretends success.</p>
      <//>`}

    ${exportOn && pane === "async" && html`
      <${Card} title="Background audit CSV" meta="long jobs" cls="mt">
        ${!seat.exportAsyncAllowed ? html`
          <${Alert} tone="info" title="Background audit not armed"
            say="Immediate downloads may still work. Background jobs stay closed until the deployment arms them."/>`
        : null}
        <button type="button" className="btn btn--go btn--sm"
          disabled=${busy || !seat.exportAsyncAllowed} onClick=${startAsyncCsv}>
          Start background CSV</button>
        <${Field} label="Job id">
          <${Input} icon="book" value=${asyncJob} onChange=${(e) => setAsyncJob(e.target.value.trim())}/>
        <//>
        <div className="btn-row" style=${{ marginTop: 8 }}>
          <button type="button" className="btn btn--sm btn--ghost"
            disabled=${busy || !asyncJob || !seat.exportAsyncAllowed} onClick=${refreshAsync}>
            Refresh status</button>
          <button type="button" className="btn btn--sm"
            disabled=${!asyncJob || !seat.exportAsyncAllowed} onClick=${downloadAsync}>
            Download result</button>
        </div>
        ${asyncStatus && html`<${Sec} title="Job status">
          <pre className="note audit-export-desk__pre">
${JSON.stringify(asyncStatus, null, 2).slice(0, 900)}</pre>
        <//>`}
        ${error && html`<${Alert} tone="bad" title="Background export error"
          say=${softAuditExportErrorOf(error, "The job did not complete.")}/>`}
      <//>`}

    ${exportOn && pane === "events" && html`
      <${Card} title="Event timeline files" meta="distinct from audit trail" cls="mt"
          sub="Events exports are separate from audit trail files.">
        <div className="btn-row audit-export-desk__acts">
          <button type="button" className="btn btn--go btn--sm"
            disabled=${!seat.exportSyncAllowed} onClick=${syncEventsCsv}>Events CSV</button>
          <button type="button" className="btn btn--sm"
            disabled=${!seat.exportSyncAllowed} onClick=${syncEventsCover}>Cover PDF</button>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!seat.exportSyncAllowed} onClick=${syncEventsRows}>Rows PDF</button>
        </div>
        <button type="button" className="btn btn--sm"
          disabled=${busy || !seat.exportAsyncAllowed} onClick=${startEventsAsync} style=${{ marginTop: 12 }}>
          Start background events CSV</button>
        <${Field} label="Events job id">
          <${Input} icon="book" value=${evJob} onChange=${(e) => setEvJob(e.target.value.trim())}/>
        <//>
        <div className="btn-row" style=${{ marginTop: 8 }}>
          <button type="button" className="btn btn--sm btn--ghost"
            disabled=${busy || !evJob || !seat.exportAsyncAllowed} onClick=${refreshEventsAsync}>
            Refresh status</button>
          <button type="button" className="btn btn--sm"
            disabled=${!evJob || !seat.exportAsyncAllowed} onClick=${downloadEventsAsync}>
            Download result</button>
        </div>
        ${evStatus && html`<${Sec} title="Events job status">
          <pre className="note audit-export-desk__pre">
${JSON.stringify(evStatus, null, 2).slice(0, 900)}</pre>
        <//>`}
      <//>`}

    ${exportOn && pane === "jobs" && html`
      <${Card} title="Durable export jobs" meta="create · status · token · download" cls="mt">
        <${Field} label="Kind">
          <select className="inp" value=${kind} onChange=${(e) => setKind(e.target.value)}>
            <option value="audit_csv">Audit spreadsheet</option>
            <option value="events_csv">Events spreadsheet</option>
          </select>
        <//>
        <${Field} label="Your reference (optional)">
          <${Input} icon="book" value=${clientRef} onChange=${(e) => setClientRef(e.target.value)}/>
        <//>
        <button type="button" className="btn btn--go btn--sm" disabled=${busy} onClick=${startCtrl} style=${{ marginTop: 8 }}>
          Create export job</button>
        <${Field} label="Job id">
          <${Input} icon="chip" value=${ctrlJob} onChange=${(e) => setCtrlJob(e.target.value.trim())}/>
        <//>
        <div className="btn-row" style=${{ marginTop: 8 }}>
          <button type="button" className="btn btn--sm btn--ghost" disabled=${busy || !ctrlJob} onClick=${refreshCtrl}>
            Refresh status</button>
          <button type="button" className="btn btn--sm" disabled=${busy || !ctrlJob} onClick=${mintToken}>
            Prepare download link</button>
          <button type="button" className="btn btn--sm" disabled=${!ctrlJob} onClick=${downloadCtrl}>
            Download</button>
        </div>
        ${ctrlToken && html`<${KV} k="Token (browser-only)" v=${ctrlToken.slice(0, 24) + "…"}/>`}
        ${ctrlStatus && html`<${Sec} title="Job status">
          <${KV} k="state" v=${String(ctrlStatus.state || "—")}/>
          <${KV} k="kind" v=${String(ctrlStatus.kind || kind)}/>
          <${KV} k="file ready" v=${ctrlStatus.has_object_key ? "yes" : "no"}/>
        <//>`}
        <p className="note">Refresh status before treating a job as complete. A file is never “ready” until the server says so.</p>
      <//>`}
  </div>`;
}

export default AuditExportDesk;
